export const formatListDate = (dateString) => {
	if (!dateString) return "N/A";
	const date = new Date(dateString);
	const day = date.getDate().toString().padStart(2, "0");
	const month = date.toLocaleString(undefined, { month: "short" });
	const year = date.getFullYear();
	return `${day} ${month} ${year}`;
};

export const formatAmount = (val) => (Number(val) || 0).toFixed(2);

export const getBrandAndSalt = (sale) => {
	const brand = sale.brandName || sale.brand || "";
	const salt =
		sale.medicineName ||
		sale.saltName ||
		sale.productName ||
		sale.itemName ||
		"";

	return {
		brand: brand || salt || "-",
		salt: brand && salt && brand !== salt ? salt : "",
	};
};

export const getProductDisplayName = (sale) => {
	const { brand, salt } = getBrandAndSalt(sale);
	if (salt) return `${brand} / ${salt}`;
	return brand;
};

export const getPatientContact = (sale) => {
	const detail = sale.patientDetail || {};

	const email =
		detail.email ||
		sale.patientEmail ||
		sale.patient?.email ||
		"";

	const phoneCode =
		detail.countryCode ||
		detail.phoneCode ||
		sale.patientPhoneCode ||
		sale.patient?.phoneCode ||
		"";

	const phoneNumber =
		detail.phone ||
		detail.phoneNumber ||
		sale.patientPhone ||
		sale.patient?.phoneNumber ||
		"";

	const phone = phoneNumber
		? `${phoneCode ? `${phoneCode} ` : ""}${phoneNumber}`.trim()
		: "";

	return {
		name:
			sale.patientName ||
			detail.patient_name ||
			detail.name ||
			"-",
		email,
		phone,
	};
};

export const getDoctorName = (sale) =>
	sale.doctorDetail?.name || sale.doctorName || "-";

export const getPrescriptionKey = (sale) =>
	sale.prescription || sale.prescriptionKey || sale.prescriptionFile || "";

export const canDownloadPrescription = (sale) =>
	Boolean(getPrescriptionKey(sale) || sale.salesId);

export const resolvePrescriptionKey = async (
	sale,
	{ getSaleInfo, clinicId, saleInfoCache }
) => {
	let key = getPrescriptionKey(sale);
	if (key) return key;

	if (!sale.salesId || !getSaleInfo) return "";

	const cacheKey = sale.salesId;
	if (saleInfoCache?.has(cacheKey)) {
		return saleInfoCache.get(cacheKey);
	}

	try {
		const response = await getSaleInfo({
			clinicId,
			salesId: sale.salesId,
		});

		if (response.data?.status === "success") {
			key = response.data.data?.prescription || "";
		}
	} catch (error) {
		console.error("Error resolving prescription key:", error);
	}

	if (saleInfoCache) {
		saleInfoCache.set(cacheKey, key);
	}

	return key;
};

export const resolvePrescriptionPresignedUrl = async (
	sale,
	{ getSaleInfo, getPresPreSignedUrl, clinicId },
	presignedUrlCache = new Map(),
	saleInfoCache = new Map()
) => {
	const cacheKey = sale.salesId || getPrescriptionKey(sale) || sale._id;
	if (cacheKey && presignedUrlCache.has(cacheKey)) {
		return presignedUrlCache.get(cacheKey);
	}

	const key = await resolvePrescriptionKey(sale, {
		getSaleInfo,
		clinicId,
		saleInfoCache,
	});

	if (!key) {
		if (cacheKey) presignedUrlCache.set(cacheKey, "");
		return "";
	}

	try {
		const response = await getPresPreSignedUrl({ key });
		const url = response.data?.data?.presignedURL || "";

		if (cacheKey) presignedUrlCache.set(cacheKey, url);
		return url;
	} catch (error) {
		console.error("Error resolving prescription URL:", error);
		if (cacheKey) presignedUrlCache.set(cacheKey, "");
		return "";
	}
};

export const getSaleAmount = (sale) =>
	sale.subTotal ?? sale.amount ?? sale.netTotal ?? sale.totalPrice ?? 0;

export const getSaleCurrency = (sale, pharmacyPOS) =>
	sale.billing?.summary?.currency ||
	sale.billing?.payment?.currency ||
	sale.currencyType ||
	sale.currency ||
	pharmacyPOS?.allowedCurrencies?.[0] ||
	"INR";

export const getSaleStatusLabel = (sale, t) => {
	if (sale.isReturn) return t("Return");
	if (sale.isSettled) return t("Settled");
	if (sale.isPaid) return t("Paid");
	if (sale.isPartialPaid) return t("Partial");
	if (sale.dueAmount > 0) return t("Pending");
	if (sale.billing?.payment?.status) {
		return String(sale.billing.payment.status).replace(/_/g, " ");
	}
	if (sale.status) return String(sale.status);
	return "-";
};

export const buildProductSalesHistoryRequest = ({
	page,
	limit,
	search = "",
	startDate = "",
	endDate = "",
}) => {
	const body = {
		page,
		limit,
		search: search.trim(),
	};

	if (startDate) body.startDate = startDate;
	if (endDate) body.endDate = endDate;

	return body;
};

export const parseProductSalesHistoryResponse = (response) => {
	const payload = response?.data?.data || {};
	return {
		list: payload.salesHistoryList || [],
		total: payload.totalSalesHistory || 0,
	};
};

export const getPharmacyDetails = (pharmacyPOS) => ({
	name:
		pharmacyPOS?.facility_name ||
		pharmacyPOS?.hospital?.name ||
		pharmacyPOS?.name ||
		"-",
	email: pharmacyPOS?.email || pharmacyPOS?.contact_person?.email || "-",
	address:
		pharmacyPOS?.address ||
		pharmacyPOS?.contact_person?.address ||
		pharmacyPOS?.hospital?.address ||
		"-",
});
